import Button from "@components/Button";
import { Column, Row } from "@components/Layout";
import type { TTSBlock } from "@components/TTSEditor/types";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import {
	DragDropProvider,
	DragDropSensors,
	type DragEventHandler,
	DragOverlay,
	SortableProvider,
	closestCenter,
	createSortable,
} from "@thisbeyond/solid-dnd";
import { FaSolidPlus } from "solid-icons/fa";
import { For, Show, type VoidComponent, createMemo, createSignal, createUniqueId } from "solid-js";
import { type SetStoreFunction, unwrap } from "solid-js/store";
import type { TTSLanguage } from "../ModeSelect/types";
import Preview from "../Preview";
import Slot from "./components/Slot";
import type { SlotProps } from "./components/Slot/types";

const Sortable: VoidComponent<Omit<SlotProps, "onDisableDrag" | "onEnableDrag">> = (props) => {
	const sortable = createSortable(props.block.id);
	const [disabled, setDisabled] = createSignal(false);
	const activators = () => (disabled() ? {} : sortable.dragActivators);
	return (
		<Row ref={sortable.ref} {...activators()} padding="5px 0">
			<Slot {...props} onDisableDrag={() => setDisabled(true)} onEnableDrag={() => setDisabled(false)} />
		</Row>
	);
};

const AdvancedMode: VoidComponent<{
	setTTSBlocks: SetStoreFunction<TTSBlock[]>;
	blocks: TTSBlock[];
	language: TTSLanguage;
	limit: number;
}> = (props) => {
	const { t } = useI18n();
	const [activeItem, setActiveItem] = createSignal<TTSBlock>();
	const ids = createMemo(() => props.blocks.map((i) => i.id));

	const handleClick = () => {
		const last = props.blocks[0];
		props.setTTSBlocks((s) => [...s, { ...last, text: "", id: createUniqueId() }]);
	};

	const onDragStart: DragEventHandler = ({ draggable }) =>
		setActiveItem(props.blocks.find((i) => i.id === draggable.id));

	const onDragEnd: DragEventHandler = ({ draggable, droppable }) => {
		if (draggable && droppable) {
			const currentItems = unwrap(props.blocks);
			const fromIndex = currentItems.findIndex((i) => i.id === draggable.id);
			const toIndex = currentItems.findIndex((i) => i.id === droppable.id);
			if (fromIndex !== toIndex) {
				const updatedItems = currentItems.slice();
				updatedItems.splice(toIndex, 0, ...updatedItems.splice(fromIndex, 1));
				props.setTTSBlocks(updatedItems);
			}
			setActiveItem(undefined);
		}
	};
	const length = createMemo(() => props.blocks.reduce((p, c) => p + c.text.length, 0));

	return (
		<>
			<Row>
				<h3>{t("app.tts.limit", length(), props.limit)}</h3>
			</Row>
			<DragDropProvider onDragStart={onDragStart} onDragEnd={onDragEnd} collisionDetector={closestCenter}>
				<DragDropSensors />
				<SortableProvider ids={ids()}>
					<Column style={{ overflow: activeItem() ? "hidden" : "auto" }}>
						<For each={props.blocks}>
							{(block, i) => (
								<Sortable
									block={block}
									onDelete={
										props.blocks.length > 1
											? () => props.setTTSBlocks((s) => s.filter((_i, b) => b !== i()))
											: undefined
									}
									onValue={(v) => props.setTTSBlocks(i(), "text", v)}
									onVoice={(v) => props.setTTSBlocks(i(), "voice", v)}
									language={props.language}
								/>
							)}
						</For>
					</Column>
				</SortableProvider>

				<DragOverlay style={{ "z-index": 64 }}>
					<Show when={activeItem()} keyed>
						{(a) => (
							<Slot
								block={a}
								onValue={() => {}}
								onVoice={() => {}}
								onDisableDrag={() => {}}
								onEnableDrag={() => {}}
								language={props.language}
							/>
						)}
					</Show>
				</DragOverlay>
			</DragDropProvider>
			<Row justifyContent="center" padding="15px 0 0" alignItems="center" gap="15px">
				<Button onclick={handleClick}>
					<FaSolidPlus />
				</Button>
				<Preview blocks={props.blocks} buttonDiameter="56px" />
			</Row>
		</>
	);
};
export default AdvancedMode;
